"use client"
import {Code as NextUiCode} from "@heroui/code";
import {Snippet as NextUiSnippet} from "@heroui/snippet";
import {Link as NextUiLink} from "@heroui/link";
import {Tooltip} from "@heroui/tooltip"
import {Divider as NextUiDivider} from "@heroui/divider"
import {Image as NextUiImage} from "@heroui/image";
import NextImage from "next/image";
import {useMonitoring} from "@/components/monitoring";
import NextLink from "next/link";

export const Paragraph = ({children}) => (
    <p className="my-4 text-base leading-7 text-foreground/90 sm:text-lg sm:leading-8">{children}</p>
)

export const Heading1 = ({children}) => (
    <h1 className="mb-6 mt-8 text-3xl font-bold leading-tight tracking-tight text-foreground sm:text-4xl lg:text-5xl">
        {children}
    </h1>
)

export const Heading2 = ({children}) => (
    <h2 className="mb-4 mt-8 text-2xl font-semibold leading-tight tracking-tight text-foreground sm:text-3xl">
        {children}
    </h2>
)

export const Heading3 = ({children}) => (
    <h3 className="mb-3 mt-8 text-xl font-semibold leading-snug text-foreground sm:text-2xl">
        {children}
    </h3>
)

export const Link = ({ isExternal, title, href, children}) => {
    const { onClickTrack} = useMonitoring()

    const link = <NextUiLink className="font-medium underline decoration-current/40 underline-offset-4" prefetch={false} as={NextLink} onClick={onClickTrack("Link", { title, href})} isExternal={isExternal} showAnchorIcon={isExternal} href={href  || '/'}>{children}</NextUiLink>;
    if(!title) {
        return link
    }

    return (<Tooltip content={title || ''}>
        {link}
    </Tooltip>)
}

export const Divider = () => <NextUiDivider className="my-10" />

export const Bold = ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>
export const Italic = ({ children }) => <i>{children}</i>
export const Underline = ({ children }) => <span className="underline decoration-current/50 underline-offset-4">{children}</span>

export const Strikethrough = ({ children }) => <span className={"line-through"}>{children}</span>
export const Code = ({ children, isInline }) => {
    if(isInline) return <NextUiCode className="whitespace-normal break-words text-sm">{children}</NextUiCode>
    return <Snippet>{children}</Snippet>
}

export const Snippet = ({ children }) => (
    <NextUiSnippet
        className="my-6 flex w-full max-w-full overflow-x-auto px-4 py-3"
        classNames={{content: "min-w-0 overflow-x-auto", pre: "whitespace-pre"}}
        symbol=""
    >
        {children}
    </NextUiSnippet>
)

export const Image = ({ url, caption, isExternal }) => (
    <figure className="my-8 flex flex-col items-center">
        <NextUiLink className="block w-full" isExternal href={url}>
            <NextUiImage
                {...(!isExternal && {as: NextImage})}
                className="h-auto w-full object-contain"
                classNames={{wrapper: "w-full max-w-full"}}
                src={url}
                alt={caption || ""}
                width={896}
                height={600}
            />
        </NextUiLink>
        {caption && (
            <figcaption className="mt-3 text-center text-sm text-foreground/60">
                <Italic>{caption}</Italic>
            </figcaption>
        )}
    </figure>
)

function getYoutubeEmbedUrl(videoUrl) {
    try {
        const url = new URL(videoUrl)
        const videoId = url.hostname === "youtu.be"
            ? url.pathname.slice(1)
            : url.searchParams.get("v") || url.pathname.split("/").filter(Boolean).pop()

        return videoId ? `https://www.youtube.com/embed/${videoId}` : videoUrl
    } catch {
        return videoUrl
    }
}

export const YoutubeEmbed = ({ videoUrl, caption }) => {
    const isYoutube = videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be');
    if(isYoutube) {
        return (
            <figure className="my-8">
                <div className="aspect-video overflow-hidden rounded-xl bg-black shadow-md">
                    <iframe
                        className="h-full w-full"
                        src={getYoutubeEmbedUrl(videoUrl)}
                        title={caption || "YouTube video player"}
                        loading="lazy"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                    />
                </div>
                {caption && <figcaption className="mt-3 text-center text-sm text-foreground/60">{caption}</figcaption>}
            </figure>
        )
    }
    return (
        <figure className="my-8 w-full">
            <video className="w-full rounded-xl" src={videoUrl} preload="metadata" controls>
            </video>
            {caption && <figcaption className="mt-3 text-center text-sm text-foreground/60">{caption}</figcaption>}
        </figure>
    )
}

export const UnorderedList = ({ children }) => <ul className="my-5 list-outside list-disc space-y-2 pl-6 text-base leading-7 text-foreground/90 marker:text-foreground/50 sm:text-lg sm:leading-8">{children}</ul>
export const OrderedList = ({ children }) => <ol className="my-5 list-outside list-decimal space-y-2 pl-6 text-base leading-7 text-foreground/90 marker:font-medium marker:text-foreground/60 sm:text-lg sm:leading-8">{children}</ol>

export const ListItem = ({ children }) => <li className="pl-1">{children}</li>

export const Table = ({children}) => (
    <div className="my-8 w-full overflow-x-auto rounded-xl border border-divider">
        <table className="w-full min-w-max border-collapse text-left text-sm">{children}</table>
    </div>
)

export const TableHeader = ({children}) => <thead className="bg-default-100 font-semibold text-foreground">{children}</thead>
export const TableBody = ({children}) => <tbody className="divide-y divide-divider">{children}</tbody>
export const TableRow = ({children}) => <tr className="transition-colors hover:bg-default-50">{children}</tr>
export const TableCol = ({children}) => <td className="border border-divider px-4 py-3 align-top">{children}</td>

export const Callout = ({children, icon}) => (
    <aside className="my-6 flex gap-3 rounded-xl border border-divider bg-default-50 p-4 sm:p-5">
        {icon && <span className="shrink-0 text-2xl" aria-hidden="true">{icon}</span>}
        <div className="min-w-0 flex-1 leading-7 text-foreground/90">{children}</div>
    </aside>
)

export const Embed = ({url}) => (
    <div className="my-8 aspect-video overflow-hidden rounded-xl border border-divider bg-black">
        <iframe
            className="h-full w-full"
            src={url}
            title="Embedded content"
            loading="lazy"
            allowFullScreen
        />
    </div>
)